export const GOAL_PROGRESS_SPECIAL_ACCOUNT_ID = "adad3a40-d28f-4dd3-8570-7a213b6cc4c7";
// export const GOAL_PROGRESS_SPECIAL_ACCOUNT_ID = "e9570019-f65a-40f9-838a-b2e31754f6ed";

export const MATRIX_ACHIEVED_BY_PROGRESS_PERCENT = Object.freeze({
  120: 150.0,
  119: 147.5,
  118: 145.0,
  117: 142.5,
  116: 140.0,
  115: 137.5,
  114: 135.0,
  113: 132.5,
  112: 130.0,
  111: 127.5,
  110: 125.0,
  109: 122.5,
  108: 120.0,
  107: 117.5,
  106: 115.0,
  105: 112.5,
  104: 110.0,
  103: 107.5,
  102: 105.0,
  101: 102.5,
  100: 100.0,
  99: 97.5,
  98: 95.0,
  97: 92.5,
  96: 90.0,
  95: 87.5,
  94: 85.0,
  93: 82.5,
  92: 80.0,
  91: 77.5,
  90: 75.0,
  89: 72.5,
  88: 70.0,
  87: 67.5,
  86: 65.0,
  85: 62.5,
  84: 60.0,
  83: 57.5,
  82: 55.0,
  81: 52.5,
  80: 50.0,
  79: 0.0
});

/**
 * Parses progress input (number or string like "25" / "25%") to a numeric percent.
 */
export function parseProgressPercent(value) {
  if (value == null) return 0;
  if (typeof value === "string") {
    const cleaned = value.trim().replace(/%$/, "");
    const parsed = Number.parseFloat(cleaned);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
}

/**
 * Clamps a value to [0, 1] for use as ProgressBar value (0 = 0%, 1 = 100% fill).
 */
export function clampProgressBarValue(value) {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

/**
 * Returns max progress for display: 200 for special account, 100 otherwise.
 * Pass accountId from CurrentUserStore.activeAccount.accountId.
 */
export function getGoalProgressMax(accountId) {
  const id = String(accountId || "").trim().toLowerCase();
  return id === GOAL_PROGRESS_SPECIAL_ACCOUNT_ID ? 200 : 100;
}

/**
 * Returns whether the given accountId is the special account (200% scale + matrix).
 */
export function isGoalProgressSpecialAccount(accountId) {
  return String(accountId || "").trim().toLowerCase() === GOAL_PROGRESS_SPECIAL_ACCOUNT_ID;
}

/**
 * Maps progress percent (79–120) to "Achieved per Matrix" percent; used for special account only.
 */
export function getMatrixAchievedPercent(progress) {
  const percent = parseProgressPercent(progress);
  const key = Math.trunc(percent);
  if (key < 79) return 0;
  if (key > 120) return 150;
  return MATRIX_ACHIEVED_BY_PROGRESS_PERCENT[key] ?? 0;
}

/**
 * Computes ProgressBar value (0–1) for a given progress percent and accountId.
 */
export function getGoalProgressBarValue(progress, accountId) {
  const max = getGoalProgressMax(accountId);
  return clampProgressBarValue(parseProgressPercent(progress) / max);
}

export function getStageConfig(progress, metricValue) {
  const percent = parseProgressPercent(progress);

  const config = {
    0: { label: "Not Started", value: 0, color: "#A1A5AD" },
    1: { label: "On Hold", value: 0, color: "#F2A724" },
    2: { label: "Not Completed", value: 0, color: "#E5453F" },
    3: { label: "In Progress", value: 50, color: "#2680EB" },
    4: { label: "Completed", value: 100, color: "#0f9960" },
    "not_started": { label: "Not Started", value: 0, color: "#A1A5AD" },
    "on_hold": { label: "On Hold", value: 0, color: "#F2A724" },
    "not_completed": { label: "Not Completed", value: 0, color: "#E5453F" },
    "in_progress": { label: "In Progress", value: 50, color: "#2680EB" },
    "completed": { label: "Completed", value: 100, color: "#0f9960" }
  };

  if (metricValue !== undefined && metricValue !== null && config[metricValue]) {
    return config[metricValue];
  }

  if (percent === 100) return config[4];
  if (percent === 50) return config[3];
  return config[0];
}

export function getProgressType(progress) {
  const percent = parseProgressPercent(progress);
  if ([0, 10, 50, 100, 90].includes(percent)) return "stage_wise";
  if (percent % 10 === 0 && percent <= 100 && percent >= 0) return "number";
  return "percentage";
}

export function getActualProgressType(goal) {
  if (goal.metric_type === 1 || goal.metric_type === "stage") return "stage_wise";
  if (goal.metric_type === 2 || goal.metric_type === "number") return "number";
  if (goal.metric_type === 0 || goal.metric_type === "percentage") return "percentage";
  return goal.progress_type || getProgressType(goal.progress);
}

export function getGoalNumericProgress(goal) {
  if (!goal) return 0;
  if (getActualProgressType(goal) === "number") {
    const progressPercent = parseProgressPercent(goal.progress);
    if (goal.progress_value !== undefined && goal.progress_value !== null) {
      const progressValue = parseProgressPercent(goal.progress_value);
      // Legacy goals may have progress_value defaulted to 0 while progress holds the real value.
      if (progressValue === 0 && progressPercent !== 0) {
        return progressPercent;
      }
      return progressValue;
    }
  }
  return parseProgressPercent(goal.progress);
}

/**
 * Weighted (or simple) average progress from sub-goals for parent goal display.
 */
export function calculateProgressFromSubGoals(subGoals) {
  if (!Array.isArray(subGoals) || subGoals.length === 0) {
    return 0;
  }

  const totalWeight = subGoals.reduce(
    (sum, sub) => sum + (parseFloat(sub.goal_weight || 0) || 0),
    0
  );

  if (totalWeight > 0) {
    const weightedSum = subGoals.reduce((sum, sub) => {
      const progress = getEffectiveProgressPercent(sub);
      const weight = parseFloat(sub.goal_weight || 0) || 0;
      return sum + progress * weight;
    }, 0);
    return weightedSum / totalWeight;
  }

  const progressSum = subGoals.reduce(
    (sum, sub) => sum + getEffectiveProgressPercent(sub),
    0
  );
  return progressSum / subGoals.length;
}

/**
 * Keep server progress authoritative; child progress is independent of its parent.
 */
export function getGoalForProgressDisplay(goal) {
  return goal;
}

export const PROGRESS_PERCENT_STATUS = Object.freeze([
  { min: 100, label: "Completed", meaning: "Fully achieved", color: "#0f9960", background: "#E3F6EC" },
  { min: 70, label: "On track", meaning: "Progressing well", color: "#1F9D61", background: "#E3F6EC" },
  { min: 40, label: "Needs attention", meaning: "Below expected pace", color: "#B8860B", background: "#FFF8E6" },
  { min: 20, label: "At risk", meaning: "Significant lag, review required", color: "#C87619", background: "#FFF0E6" },
  { min: 0, label: "Critical", meaning: "Urgent intervention needed", color: "#db3737", background: "#FDEDED" }
]);

/**
 * Maps a 0–100+ progress percent to a read-only status label and styling.
 */
export function getProgressStatusFromPercent(percent) {
  const value = parseProgressPercent(percent);
  if (value >= 100) return PROGRESS_PERCENT_STATUS[0];
  if (value >= 70) return PROGRESS_PERCENT_STATUS[1];
  if (value >= 40) return PROGRESS_PERCENT_STATUS[2];
  if (value >= 20) return PROGRESS_PERCENT_STATUS[3];
  return PROGRESS_PERCENT_STATUS[4];
}

/**
 * Normalizes goal/sub-goal progress to a 0–100 percent for status labeling.
 */
export function getEffectiveProgressPercent(goal) {
  if (!goal) return 0;

  const progressType = getActualProgressType(goal);
  const rawProgress = getGoalNumericProgress(goal);

  if (progressType === "stage_wise") {
    const isStageCode = [0, 1, 2, 3, 4].includes(rawProgress);
    if (isStageCode) {
      return getStageConfig(rawProgress, goal.metric_value).value;
    }
    return Math.max(0, Math.min(100, rawProgress));
  }

  if (progressType === "number") {
    const min = goal.metric_start_range ?? 0;
    const max = goal.metric_end_range ?? 100;
    const value = getGoalNumericProgress(goal);
    if (max > min) {
      return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
    }
    if (max === min && value >= max) return 100;
    return 0;
  }

  return Math.max(0, rawProgress);
}

export function getProgressStatusForGoal(goal) {
  return getProgressStatusFromPercent(getEffectiveProgressPercent(goal));
}

/**
 * Formats progress as a 0–100% string for display next to the status label.
 * Number metric type stores a raw value in range [min, max]; display uses normalized percent.
 */
export function formatGoalProgressPercent(goal) {
  if (!goal) return "0%";

  const display = getEffectiveProgressPercent(goal);
  const rounded = Number.isInteger(display) ? display : Math.round(display * 10) / 10;
  return `${rounded}%`;
}

export function getStageName(progress, metricValue) {
  const percent = parseProgressPercent(progress);
  if (metricValue !== undefined && metricValue !== null) {
    const stageMap = {
      0: "Not Started",
      1: "On Hold",
      2: "Not Completed",
      3: "In Progress",
      4: "Completed",
      "not_started": "Not Started",
      "on_hold": "On Hold",
      "not_completed": "Not Completed",
      "in_progress": "In Progress",
      "completed": "Completed"
    };
    if (stageMap[metricValue]) return stageMap[metricValue];
  }

  if (percent === 100) return "Completed";
  if (percent === 50) return "In Progress";
  return "Not Started";
}
